

import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, LogOut, Settings, Shield, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { createClient } from '@/lib/supabase/client';

interface ProfileDropdownProps {
  displayName?: string;
  isAdmin?: boolean;
}

export function ProfileDropdown({ displayName, isAdmin }: ProfileDropdownProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    const supabase = createClient();
    await supabase.auth.signOut();
    navigate('/login');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Profile Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'p-2.5 rounded-full bg-neutral-900 border border-neutral-800',
          'hover:bg-neutral-800 hover:border-neutral-700 transition-all duration-200',
          'focus:outline-none focus:ring-2 focus:ring-neutral-600 focus:ring-offset-2 focus:ring-offset-black',
          isOpen && 'bg-neutral-800 border-neutral-700'
        )}
        aria-label="Open profile menu"
        aria-expanded={isOpen}
      >
        <User className="w-5 h-5 text-neutral-300" />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={cn(
            'absolute right-0 top-full mt-2 w-56',
            'bg-neutral-900 border border-neutral-800 rounded-xl',
            'shadow-xl shadow-black/50',
            'overflow-hidden',
            'animate-in fade-in slide-in-from-top-2 duration-200'
          )}
        >
          {/* User Info Header */}
          {displayName && (
            <div className="px-4 py-3 border-b border-neutral-800">
              <p className="text-sm font-medium text-white truncate">{displayName}</p>
              <p className="text-xs text-neutral-500">Manage your account</p>
            </div>
          )}

          {/* Menu Items */}
          <div className="py-1">
            <Link
              to="/profile"
              onClick={() => setIsOpen(false)}
              className={cn(
                'flex items-center gap-3 px-4 py-2.5 w-full text-left',
                'text-sm text-neutral-300 hover:text-white',
                'hover:bg-neutral-800/80 transition-colors'
              )}
            >
              <Settings className="w-4 h-4" />
              <span>Profile Settings</span>
            </Link>

            {isAdmin && (
              <Link
                to="/admin"
                onClick={() => setIsOpen(false)}
                className={cn(
                  'flex items-center gap-3 px-4 py-2.5 w-full text-left',
                  'text-sm text-neutral-300 hover:text-white',
                  'hover:bg-neutral-800/80 transition-colors'
                )}
              >
                <Shield className="w-4 h-4" />
                <span>Admin Panel</span>
              </Link>
            )}

            <button
              onClick={handleSignOut}
              disabled={isSigningOut}
              className={cn(
                'flex items-center gap-3 px-4 py-2.5 w-full text-left',
                'text-sm text-neutral-300 hover:text-white',
                'hover:bg-neutral-800/80 transition-colors',
                isSigningOut && 'opacity-50 cursor-not-allowed'
              )}
            >
              {isSigningOut ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <LogOut className="w-4 h-4" />
              )}
              <span>{isSigningOut ? 'Signing out...' : 'Sign Out'}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
